import axios from 'axios'
import configApp from '@/utils/appConfig'
import { featureAccess } from '@/utils/accesses'
import { allBaselineChains, readyBaselineChains, chainsFingerprint } from '@/utils/baseline/chains'
import { readCachedBaselineChains, writeCachedBaselineChains } from '@/utils/baseline/chains-cache'

// Own axios instance: the baseline service sits outside the main API and must not
// pick up the app's auth/cancellation interceptors.
const baselineClient = axios.create({ baseURL: configApp.VUE_APP_BASELINE_API_URL })

// Poll timer lives outside the store — it is not state anyone renders.
let healthTimer = null

// The in-flight request. `allChainsLoaded` only turns true after the await, so
// without this the chain filter and the log tab each fetch the same payload.
let allChainsRequest = null

// No app interceptors on this client, so credentials go in by hand. Two callers,
// two credentials: the browser has the session bearer (read from localStorage,
// like api-client - the store may not be hydrated yet), while a PDF render
// started with an API key has no bearer at all and must send the key instead
// (GLBA-13482).
const authHeaders = state => {
  const headers = {}
  const token = localStorage.getItem('token')

  if (token) headers.Authorization = `Bearer ${token}`
  if (state?.apiKey) headers['x-api-key'] = state.apiKey

  return headers
}

export default {
  // Commits only when the offered set changed, so the poll never disturbs an
  // open dropdown or a running check.
  async fetchChains({ commit, state }, { silent = false } = {}) {
    if (!silent) commit('SET_CHAINS_LOADING', true)

    try {
      const { data: payload } = await baselineClient.get('/health', { headers: authHeaders(state) })
      const chains = readyBaselineChains(payload?.data?.chains)

      commit('SET_HEALTH_STATUS', payload?.data?.status ?? null)
      commit('SET_HEALTH_ERROR', null)

      if (chainsFingerprint(chains) !== chainsFingerprint(state.chains)) {
        commit('SET_CHAINS', chains)
      }

      commit('SET_CHAINS_LOADED', true)
      return chains
    } catch (error) {
      commit('SET_HEALTH_ERROR', error?.message || 'Baseline health is unavailable')
      // A failed poll keeps the previous list rather than emptying the dropdown.
      return state.chains
    } finally {
      if (!silent) commit('SET_CHAINS_LOADING', false)
    }
  },

  // The same payload as `fetchChains`, unfiltered: a filter has to name every
  // chain that could appear in the rows, including one that is down right now.
  //
  // Stale-while-revalidate - the stored copy classifies rows on first paint, one
  // network answer per session replaces it. Not polled like `fetchChains`,
  // because this list changes when a chain is added, not when one goes down.
  //
  // Baseline chains come from screening, so with the Screening flag off there is
  // nothing to classify and the service is not asked at all - not even the cache.
  async fetchAllChains({ commit, state }, { force = false } = {}) {
    if (!featureAccess('SCREENING')) return []

    if (!state.allChains.length) {
      const cached = readCachedBaselineChains()
      if (cached.length) commit('SET_ALL_CHAINS', cached)
    }

    if (state.allChainsLoaded && !force) return state.allChains
    if (allChainsRequest && !force) return allChainsRequest

    // Only an empty list is worth a spinner; revalidating must not blank one.
    const silent = state.allChains.length > 0
    if (!silent) commit('SET_ALL_CHAINS_LOADING', true)

    allChainsRequest = (async () => {
      try {
        const { data: payload } = await baselineClient.get('/health', { headers: authHeaders(state) })
        const chains = allBaselineChains(payload?.data?.chains)

        commit('SET_ALL_CHAINS', chains)
        commit('SET_ALL_CHAINS_LOADED', true)
        writeCachedBaselineChains(chains)
        return chains
      } catch (error) {
        return state.allChains
      } finally {
        if (!silent) commit('SET_ALL_CHAINS_LOADING', false)
        allChainsRequest = null
      }
    })()

    return allChainsRequest
  },

  // Awaits the first load so callers can rely on the list being there; every
  // repeat runs silently in the background.
  async startChainsPolling({ dispatch }) {
    dispatch('stopChainsPolling')
    await dispatch('fetchChains')

    healthTimer = setInterval(
      () => dispatch('fetchChains', { silent: true }),
      configApp.VUE_APP_BASELINE_HEALTH_POLL_MS,
    )
  },

  stopChainsPolling() {
    if (healthTimer) {
      clearInterval(healthTimer)
      healthTimer = null
    }
  },

  // One screening check. `type` is the path segment: 'address' or 'tx_hash'.
  async getBaselineReport({ state }, { type, value, chain }) {
    const { data: payload } = await baselineClient.get(
      `/${type}/${encodeURIComponent(value)}`,
      { params: { chain }, headers: authHeaders(state) },
    )

    return payload?.data ?? null
  },

  // Per-address profile behind an expanded participant row - a second request,
  // addresses only, fetched lazily.
  async getAddressAnalysis({ state }, { address, chain }) {
    const { data: payload } = await baselineClient.get(
      `/analysis/${encodeURIComponent(address)}`,
      { params: { chain }, headers: authHeaders(state) },
    )

    return payload?.data ?? null
  },
}
